import React, { useEffect, useState } from "react";
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  TruckIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  TagIcon,
  HashtagIcon,
} from "@heroicons/react/24/outline";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from 'react-redux';
import { acceptRejectOrder, newOrderDetails } from '@/store/slices/newDeliverySlice';
import moment from 'moment';
import Swal from "sweetalert2";
import Toaster from "@/components/Toaster";
import ShowLoader from "@/components/Loader";
import OrderDeclineModal from "../modals/order-decline";
import { useTranslation } from "react-i18next";

export function NewDeliveryOverview() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t } = useTranslation("delivery");
  const { new_order_details, loading } = useSelector((state) => state.delivery);
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    if (id) dispatch(newOrderDetails({ order_id: id }));
  }, [id, dispatch]);

  const confirmAcceptOrder = () => {
    Swal.fire({
      title: t("accept_order_q"),
      text: t("accept_order_confirm_text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("yes_accept"),
      cancelButtonText: t("cancel"),
      background: "#ffffff",
      color: "#2F302C",
      buttonsStyling: false,
      customClass: {
        popup: "rounded-2xl shadow-2xl",
        confirmButton: "rounded-xl px-6 py-3 font-semibold text-white bg-[#F4B11F] mx-2",
        cancelButton: "rounded-xl px-6 py-3 font-medium text-[#2F302C] bg-gray-200 mx-2",
        title: "text-lg font-semibold",
        htmlContainer: "text-sm text-gray-500",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        acceptDeclineOrder('approved');
      }
    });
  };

  const acceptDeclineOrder = (type, reason = '') => {
    dispatch(acceptRejectOrder({ status: type, description: reason, order_id: new_order_details?.id }))
      .unwrap()
      .then((res) => {
        if (res?.status) {
          let status = type === 'approved' ? t('accepted') : t('declined');
          Toaster.success(res?.message || t("order_status_success", { status }));
          navigate('/delivery-partner/home');
        } else {
          Toaster.error(res?.message || t("something_went_wrong"));
        }
      })
      .catch((err) => {
        Toaster.error(err?.message || t("something_went_wrong"));
      });
  };

  const products = new_order_details?.products || [];
  const [expandedNotes, setExpandedNotes] = useState({});

  const toggleNote = (productId) => {
    setExpandedNotes((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  return (
    <div className="flex flex-col min-h-screen">
      {loading && <ShowLoader />}

      {/* Header */}
      <div className="w-full bg-[#065C36] py-6 text-white px-4 sm:px-6">
        <div className="max-w-7xl mx-auto relative">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-xs sm:text-sm font-light hover:underline text-left max-w-[85%] sm:max-w-none"
          >
            <ArrowLeftIcon className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">{t("back_to_listing")}</span>
            <span className="sm:hidden">{t("back")}</span>
          </button>
          <h1 className="text-2xl font-semibold text-center">
            #{new_order_details?.order_id || ''}
          </h1>
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto py-6 sm:py-10 px-4 sm:px-6 space-y-6 flex-grow">

        {/* Created On */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl px-5 py-4 shadow-sm border border-[rgba(47,48,44,0.06)]">
          <div className="flex items-center gap-3">
            <img
              src="/img/icons/created.png"
              alt="Created Icon"
              className="h-5 w-5 object-contain shrink-0"
            />
            <p className="text-sm text-gray-600">
              {t("created_on")}{" "}
              <span className="font-semibold text-gray-800">
                {new_order_details?.order_created_date
                  ? moment(new_order_details?.order_created_date).format("DD/MM/YYYY - hh:mm A")
                  : t("not_available")}
              </span>
            </p>
          </div>
          {/* <div className="text-sm text-gray-600 sm:text-right">
            <span className="text-gray-500">Delivery Charge </span>{" "}SAR :{" "}
            <span className="font-semibold text-gray-800">
              {new_order_details?.delivery_charge
                ? Number(new_order_details?.delivery_charge).toFixed(2)
                : '0.00'}
            </span>
          </div> */}
        </div>

        {/* Delivery Date & Deadline */}
        <div className="flex items-center gap-3 bg-[rgba(6,92,54,0.06)] rounded-xl px-5 py-4 border border-[rgba(6,92,54,0.08)]">
          <img
            src="/img/icons/calender.png"
            alt="Delivery Date"
            className="h-10 w-10 object-contain shrink-0"
          />
          <div className="flex flex-1 flex-col sm:flex-row sm:items-center justify-between gap-y-2">
            <p className="text-sm text-gray-700">
              {t("to_be_delivered_on")}{" "}
              <span className="font-semibold text-gray-900">
                {[...new Set(new_order_details?.delivery_locations?.map(d => d?.deadline))]
                  ?.filter(Boolean)
                  ?.map((date, i, arr) => (
                    <span key={i}>
                      {moment(date).format("DD/MM/YYYY - hh:mm A")}
                      {i !== arr.length - 1 ? ", " : ""}
                    </span>
                  ))}
              </span>
            </p>
            {new_order_details?.delivery_locations?.[0]?.priority && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">{t("priority")}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase text-white shadow-sm ${new_order_details.delivery_locations[0].priority === 'high' ? 'bg-red-500' :
                  new_order_details.delivery_locations[0].priority === 'medium' ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}>
                  {new_order_details.delivery_locations[0].priority}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Installation / Delivery Type */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl px-5 py-4 shadow-sm border border-[rgba(47,48,44,0.06)]">
          <div className="flex items-center gap-3">
            {new_order_details?.is_installation === "yes" && (
              <>
                <img src="/img/icons/Container-3.png" alt="" className="h-5 w-5 object-contain shrink-0" />
                <span className="text-[15px] font-bold text-[#F4B11F]">{t("delivery_and_installation")}</span>
              </>
            )}
            {new_order_details?.is_installation === "no" && (
              <>
                <TruckIcon className="h-5 w-5 text-[#F4B11F] shrink-0" />
                <span className="text-[15px] font-bold text-[#F4B11F]">{t("delivery")}</span>
              </>
            )}
          </div>
          <div className="text-sm text-gray-600 sm:text-right">
            {new_order_details?.is_installation === "yes" && (
              <>
                <span className="text-gray-500">{t("installation_charge")} </span>{" "} {user?.currency_code} :{" "}
                <span className="font-semibold text-gray-800">
                  {new_order_details?.installation_charge
                    ? Number(new_order_details?.installation_charge).toFixed(2)
                    : '0.00'}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Highlight Card - Total Items */}
        <div
          className="rounded-xl px-6 py-5 text-white shadow-md flex items-center justify-start"
          style={{ background: "linear-gradient(90deg, rgba(244,177,31,1) 0%, rgba(244,177,31,0.6) 100%)" }}
        >
          <p className="text-sm opacity-90">{t("total_items_to_collect")}</p>
          <h2 className="text-xl font-bold ml-2">{new_order_details?.items_count || 0} {t("items")}</h2>
        </div>

        {/* Price Breakdown Strip (Receipt Style) */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-[rgba(47,48,44,0.06)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-6 w-1 rounded-full bg-[#065C36]" />
            <h2 className="text-base font-bold text-gray-800">{t("price_breakdown")}</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-gray-500">
                <TruckIcon className="h-4 w-4 text-[#065C36]" />
                {t("delivery_charge")}
              </span>
              <span className="font-semibold text-gray-800">
               {new_order_details?.rfq_currency_code} {Number(new_order_details?.price_summary?.total_delivery_charge ?? 0).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-gray-500">
                <img src="/img/icons/Container-3.png" alt="" className="h-4 w-4 object-contain" />
                {t("installation_charge")}
              </span>
              <span className="font-semibold text-gray-800">
                {user?.currency_code} {Number(new_order_details?.price_summary?.total_installation_charge ?? 0).toFixed(2)}
              </span>
            </div>
            <div className="border-t border-dashed border-gray-200 my-2" />
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-800">{t("total_amount")}</span>
              <span className="text-xl font-bold text-[#F4B11F]">
                {new_order_details?.rfq_currency_code} {Number(new_order_details?.price_summary?.grand_total ?? 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
        {/* Recipient Details */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-[rgba(47,48,44,0.06)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-6 w-1 rounded-full bg-[#065C36]" />
            <h2 className="text-base font-bold text-gray-800">{t("recipient_details")}</h2>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 text-sm text-gray-600">
            <p>
              <span className="text-gray-500">{t("name")} </span>
              <span className="font-semibold text-gray-800">
                {new_order_details?.recipient_details?.full_name || t("not_available")}
              </span>
            </p>
            <p>
              <span className="text-gray-500">{t("mobile_number")} </span>
              <span className="font-semibold text-gray-800">
                {new_order_details?.recipient_details?.country_code || ""} {new_order_details?.recipient_details?.mobile_number || t("not_available")}
              </span>
            </p>
          </div>
        </div>

        {/* Vendors & Drop Locations Count */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-[rgba(47,48,44,0.06)] flex items-center gap-4">
            <img src="/img/icons/cake.png" alt="vendors" className="h-6 w-6 object-contain" />
            <div>
              <p className="text-xl text-gray-500">
                {t("num_of_vendors")}{" "}
                <span className="text-2xl font-bold text-gray-800">
                  {new_order_details?.vendor_locations?.length || '0'} {t("vendors")}
                </span>
              </p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-[rgba(47,48,44,0.06)] flex items-center gap-4">
            <img src="/img/icons/cake.png" alt="vendors" className="h-6 w-6 object-contain" />
            <div>
              <p className="text-xl text-gray-500">
                {t("num_of_drop_off_locations")}{" "}
                <span className="text-2xl font-bold text-gray-800">
                  {new_order_details?.delivery_locations?.length || '0'} {t("locations")}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Vendor & Drop-off Locations */}
        <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          {/* Vendors Column */}
          <div className="space-y-6">
            {new_order_details?.vendor_locations?.map((vendor, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-[rgba(47,48,44,0.06)]">
                <div className="flex items-center gap-2 mb-3">
                  <MapPinIcon className="h-5 w-5 text-grey-300" />
                  <p className="text-xs font-bold tracking-wider text-gray-400">{t("nearest_vendor_location")}</p>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${vendor.latitude},${vendor.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-blue-600 cursor-pointer transition"
                  title={t("open_in_google_maps")}
                >
                  {vendor?.pickup_landmark || ""}
                </a>
                <p className="text-sm text-gray-600 mt-1">{vendor?.pickup_location || ""}</p>
              </div>
            ))}
          </div>

          {/* Customer Column */}
          <div className="space-y-6">
            {new_order_details?.delivery_locations?.map((delivery, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-[rgba(47,48,44,0.06)]">
                <div className="flex items-center gap-2 mb-3">
                  <MapPinIcon className="h-5 w-5 text-grey-300" />
                  <p className="text-sm text-gray-500">{t("nearest_drop_off_location")}</p>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${delivery.latitude},${delivery.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-blue-600 cursor-pointer transition"
                  title={t("open_in_google_maps")}
                >
                  {delivery?.delivery_landmark || ""}
                </a>
                <p className="text-sm text-gray-600 mt-1">{delivery?.delivery_address || ""}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── PRODUCTS SECTION ── */}
        {products.length > 0 && (
          <div>
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="h-6 w-1 rounded-full bg-[#065C36]" />
              <h2 className="text-lg font-bold text-gray-800">{t("products_to_be_delivered")}</h2>
              <span className="ml-1 px-2.5 py-0.5 rounded-full bg-[rgba(6,92,54,0.08)] text-[#065C36] text-xs font-bold">
                {products.length} {products.length === 1 ? t('product') : t('products')}
              </span>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((product, index) => (
                <div
                  key={product.id ?? index}
                  className="bg-white rounded-xl p-5 shadow-sm border border-[rgba(47,48,44,0.06)] flex flex-col gap-4"
                >
                  {/* Product Image + Name */}
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 rounded-lg border border-gray-200 flex items-center justify-center p-2 shrink-0">
                      <img
                        src={product.image || "/img/photo-gallery.png"}
                        alt={product.product_name || "Product"}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => { e.target.src = "/img/photo-gallery.png"; }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2">
                        {product.product_name}
                      </h3>
                      <span className="inline-block mt-1.5 px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 text-[10px] font-bold tracking-widest uppercase">
                        {product.sku_number}
                      </span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-50" />

                  {/* Details */}
                  <div className="space-y-2.5">
                    {/* Category */}
                    <div className="flex items-center gap-2.5">
                      <div className="h-7 w-7 rounded-lg bg-[rgba(6,92,54,0.08)] flex items-center justify-center shrink-0">
                        <img src="/img/icons/Containers.png" alt="" className="h-5 w-3.5 text-[#065C36]" />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{t("category")}</p>
                        <p className="text-xs font-semibold text-gray-700">{product.category || t("not_available")}</p>
                      </div>
                    </div>

                    {/* Quantity */}
                    <div className="flex items-center gap-2.5">
                      <div className="h-7 w-7 rounded-lg bg-[rgba(244,177,31,0.10)] flex items-center justify-center shrink-0">
                        <CubeIcon className="h-3.5 w-3.5 text-[#F4B11F]" />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{t("quantity")}</p>
                        <p className="text-xs font-semibold text-gray-700">{product.quantity ?? t("not_available")} {t("units")}</p>
                      </div>
                    </div>

                    {/* Delivery Charge */}
                    <div className="flex items-center gap-2.5">
                      <div className="h-7 w-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <TruckIcon className="h-3.5 w-3.5 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{t("delivery_charge")}</p>
                        <p className="text-xs font-semibold text-gray-700">{new_order_details?.rfq_currency_code} {Number(product.delivery_charge ?? 0).toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Installation Charge — only if non-zero */}
                    {Number(product.assigned_installtion_chagrge) > 0 && (
                      <div className="flex items-center gap-2.5">
                        <div className="h-7 w-7 rounded-lg bg-[rgba(244,177,31,0.10)] flex items-center justify-center shrink-0">
                          <img src="/img/icons/Container-3.png" alt="" className="h-3.5 w-3.5 object-contain" />
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{t("installation_charge")}</p>
                          <p className="text-xs font-semibold text-gray-700">{user?.currency_code} {Number(product.assigned_installtion_chagrge).toFixed(2)}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Receipt-style per-unit breakdown */}
                  <div className="rounded-lg bg-[rgba(6,92,54,0.04)] border border-[rgba(6,92,54,0.08)] p-3 space-y-2">
                    {/* Delivery Charge Line */}
                    <div className="flex items-center justify-between text-[11px] text-gray-600">
                      <span>
                        {t("delivery_charge")}{" "}
                        <span className="text-gray-400">
                          ({product.quantity ?? 0} {t("units")} × {new_order_details?.rfq_currency_code} {Number(product.delivery_charge_per_unit ?? 0).toFixed(2)})
                        </span>
                      </span>
                      <span className="font-medium text-gray-800">
                        {new_order_details?.rfq_currency_code} {Number(product.delivery_charge ?? 0).toFixed(2)}
                      </span>
                    </div>

                    {/* Installation Charge Line */}
                    {Number(product.installation_charge_per_unit) > 0 && (
                      <div className="flex items-center justify-between text-[11px] text-gray-600">
                        <span>
                          {t("installation_charge")}{" "}
                          <span className="text-gray-400">
                            ({product.quantity ?? 0} {t("units")} × {user?.currency_code} {Number(product.installation_charge_per_unit).toFixed(2)})
                          </span>
                        </span>
                        <span className="font-medium text-gray-800">
                          {new_order_details?.rfq_currency_code} {Number(product.assigned_installtion_chagrge ?? 0).toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Line Total */}
                    <div className="flex items-center justify-between text-xs font-bold pt-2 mt-1 border-t border-dashed border-[rgba(6,92,54,0.15)]">
                      <span className="text-[#065C36]">{t("line_total")}</span>
                      <span className="text-[#F4B11F]">
                        {new_order_details?.rfq_currency_code} {Number(product.product_total_charge ?? 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {product.delivery_note && (
                    <div className="mt-1 px-3 py-2 rounded-lg bg-[rgba(6,92,54,0.04)] border border-[rgba(6,92,54,0.08)]">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5">{t("delivery_note")}</p>
                      <p
                        className={`text-xs text-gray-700 ${expandedNotes[product.id] ? "" : "line-clamp-2"
                          }`}
                      >
                        {product.delivery_note}
                      </p>
                      {product.delivery_note.length > 80 && (
                        <button
                          type="button"
                          onClick={() => toggleNote(product.id)}
                          className="mt-1 text-[10px] font-bold text-[#065C36] hover:underline"
                        >
                          {expandedNotes[product.id] ? t("view_less") : t("view_more")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* ── END PRODUCTS SECTION ── */}

      </div>

      {/* Footer Buttons */}
      <div className="w-full bg-[#065C36] py-5 sm:py-6 px-4 sm:px-6 pb-[max(1.25rem,env(safe-area-inset-bottom,0px))]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-3 sm:gap-6">
          <button
            type="button"
            className="flex-1 border border-white text-white py-3.5 rounded-xl font-medium hover:bg-white/10 transition"
            onClick={() => setIsModalOpen(true)}
          >
            {t("decline")}
          </button>
          <button
            type="button"
            className="flex-1 bg-[#F4B11F] text-white py-3.5 rounded-xl font-medium hover:opacity-90 transition"
            onClick={() => confirmAcceptOrder()}
          >
            {t("accept")}
          </button>
        </div>
      </div>

      <OrderDeclineModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onDecline={(reason) => {
          setIsModalOpen(false);
          acceptDeclineOrder("rejected", reason);
        }}
      />
    </div>
  );
}

export default NewDeliveryOverview;